import React, { useMemo, useCallback } from 'react';
import { useRole } from '../../context/roleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEdit,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import DataTable from '../common/DataTable';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * HsnTable
 *
 * A configured DataTable for displaying HSN codes and GST rates. Provides
 * selection, pagination and action callbacks for viewing, editing and
 * deleting entries.
 *
 * Props:
 * - hsnCodes: array of HSN objects
 * - loading: boolean to show skeleton rows
 * - selectedHsnCodes: Set of selected ids
 * - onSelectHsnCode/onSelectAll: selection callbacks
 * - onViewHsnCode/onEditHsnCode/onDeleteHsnCode: action callbacks
 */
const HsnTable = ({
  hsnCodes = [],
  loading = false,
  selectedHsnCodes = new Set(),
  onSelectHsnCode,
  onSelectAll,
  onViewHsnCode,
  onEditHsnCode,
  onDeleteHsnCode,
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess } = useRole();
  const hasHsnCodes = Array.isArray(hsnCodes) && hsnCodes.length > 0;

  const hoverColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.4),
    []
  );

  /**
   * formatPercentage(value)
   *
   * Utility to format numeric GST/tax rates showing two decimal places and
   * appending a percent sign. Returns '0%' for invalid numbers.
   */
  const formatPercentage = useCallback((value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0%';
    }
    return `${Number(value).toFixed(2)}%`;
  }, []);

  const allSelected = hasHsnCodes && hsnCodes.length > 0 && hsnCodes.every(hsn => selectedHsnCodes.has(hsn.id));

  const columns = useMemo(
    () => [
      // Selection column
      {
        id: 'select',
        width: '50px',
        align: 'center',
        alignItems: 'center',
        sortable: false,
        renderHeader: () => (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => onSelectAll?.(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            aria-label={t('hsnCodes.table.aria.selectAll')}
          />
        ),
        renderCell: (hsnCode) => (
          <input
            type="checkbox"
            checked={selectedHsnCodes.has(hsnCode.id)}
            onChange={(e) => onSelectHsnCode?.(hsnCode.id, e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            aria-label={t('hsnCodes.table.aria.selectHsnCode')}
          />
        ),
        skeleton: <div className="h-4 w-4 rounded bg-gray-300 mx-auto" />,
      },
      {
        id: 'hsn_code',
        label: t('hsnCodes.table.columns.hsnCode'),
        sortable: true,
        align: 'left',
        sortAccessor: (hsnCode) => hsnCode?.hsn_code || '',
        renderCell: (hsnCode) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-blue-700">
              {hsnCode.hsn_code || 'N/A'}
            </span>
          </div>
        ),
        skeleton: <div className="h-5 w-20 rounded bg-gray-300" />,
      },
      {
        id: 'hsn_description',
        label: t('hsnCodes.table.columns.description'),
        sortable: true,
        align: 'left',
        sortAccessor: (hsnCode) => hsnCode?.hsn_description || '',
        renderCell: (hsnCode) => (
          <div className="flex items-start gap-2">
            <span className="text-gray-700 line-clamp-2">
              {hsnCode.hsn_description || 'No description'}
            </span>
          </div>
        ),
        skeleton: <div className="h-5 w-32 rounded bg-gray-300" />,
      },
      {
        id: 'gst_rate',
        label: t('hsnCodes.table.columns.tax'),
        sortable: true,
        align: 'center',
        sortAccessor: (hsnCode) => Number(hsnCode?.gst_rate || 0),
        renderCell: (hsnCode) => (
          <div className="flex items-center justify-center gap-2">
            <span className="font-medium text-green-700">
              {formatPercentage(hsnCode.gst_rate) || 'N/A'}
            </span>
          </div>
        ),
        skeleton: <div className="h-5 w-12 rounded bg-gray-300 mx-auto" />,
      },
      {
        id: 'actions',
        label: t('hsnCodes.table.columns.actions'),
        width: '120px',
        align: 'center',
        alignItems: 'center',
        sortable: false,
        renderCell: (hsnCode) => (
          <div className="flex items-center justify-center gap-2">
            {onViewHsnCode && (
              <button
                onClick={() => onViewHsnCode(hsnCode)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.1),
                  color: UI_COLORS.NAVIGATION.ACTIVE,
                }}
                title={t('hsnCodes.table.aria.viewHsnCode')}
                aria-label={t('hsnCodes.table.aria.viewHsnCode')}
              >
                <FontAwesomeIcon icon={faEye} className="text-sm" />
              </button>
            )}
            {onEditHsnCode && (
              <button
                onClick={() => onEditHsnCode(hsnCode)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.WARNING,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
                }}
                title={t('hsnCodes.table.aria.editHsnCode')}
                aria-label={t('hsnCodes.table.aria.editHsnCode')}
              >
                <FontAwesomeIcon icon={faEdit} className="text-sm" />
              </button>
            )}
            {onDeleteHsnCode && checkDeleteAccess() && (
              <button
                onClick={() => onDeleteHsnCode(hsnCode)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.ERROR,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                }}
                title={t('hsnCodes.table.aria.deleteHsnCode')}
                aria-label={t('hsnCodes.table.aria.deleteHsnCode')}
              >
                <FontAwesomeIcon icon={faTrash} className="text-sm" />
              </button>
            )}
          </div>
        ),
        skeleton: (
          <div className="flex items-center justify-center gap-2">
            <div className="h-4 w-4 rounded bg-gray-300" />
            <div className="h-4 w-4 rounded bg-gray-300" />
            <div className="h-4 w-4 rounded bg-gray-300" />
          </div>
        ),
      },
    ],
    [
      selectedHsnCodes,
      allSelected,
      onSelectHsnCode,
      onSelectAll,
      onViewHsnCode,
      onEditHsnCode,
      onDeleteHsnCode,
      formatPercentage,
      checkDeleteAccess,
      t,
    ]
  );

  /**
   * getRowStyle(hsnCode)
   *
   * Returns style overrides for a row (highlight when selected, hover
   * styling). Passed into DataTable as `getRowStyle`.
   */
  const getRowStyle = useCallback(
    (hsnCode) => {
      const isSelected = selectedHsnCodes.has(hsnCode.id);
      return {
        backgroundColor: isSelected
          ? colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.1)
          : 'transparent',
        '&:hover': {
          backgroundColor: isSelected
            ? colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.15)
            : hoverColor,
        },
      };
    },
    [hoverColor, selectedHsnCodes]
  );

  const emptyStateConfig = useMemo(
    () => ({
      title: t('hsnCodes.table.emptyState.title'),
      message: t('hsnCodes.table.emptyState.message'),
    }),
    [t]
  );

  return (
    <DataTable
      data={hsnCodes}
      columns={columns}
      loading={loading}
      emptyState={emptyStateConfig}
      getRowStyle={getRowStyle}
      enablePagination={false}
      className="hsn-codes-table"
    />
  );
};

export default HsnTable;